import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Home</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-gray-600 mb-8">
            <strong>Last Updated:</strong> December 22, 2025
          </p>

          <div className="prose prose-blue max-w-none space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-700 leading-relaxed">
                Welcome to Understanding Grief ("we," "our," or "us"). We are committed to protecting your privacy 
                and being transparent about how we collect, use, and share information. This Privacy Policy explains 
                our practices regarding your personal information when you visit our website at{' '}
                <span className="font-medium">grief-guide-1.preview.emergentagent.com</span>.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                By using our website, you agree to the collection and use of information in accordance with this policy.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">2.1 Automatically Collected Information</h3>
              <p className="text-gray-700 leading-relaxed">
                When you visit our website, we automatically collect certain information about your device and 
                browsing behavior, including:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mt-3 ml-4">
                <li>IP address</li>
                <li>Browser type and version</li>
                <li>Device type (mobile, desktop, tablet)</li>
                <li>Operating system</li>
                <li>Pages visited and time spent on pages</li>
                <li>Referring website</li>
                <li>Date and time of visit</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">2.2 Cookies and Tracking Technologies</h3>
              <p className="text-gray-700 leading-relaxed">
                We use cookies and similar tracking technologies to track activity on our website and store 
                certain information. Cookies are small data files that may include an anonymous unique identifier.
              </p>
            </section>

            {/* Google AdSense */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Google AdSense and Advertising</h2>
              <p className="text-gray-700 leading-relaxed">
                We use Google AdSense to display advertisements on our website. Google AdSense uses cookies to 
                serve ads based on your prior visits to our website or other websites on the internet.
              </p>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.1 How Google Uses Information</h3>
              <p className="text-gray-700 leading-relaxed">
                Google uses cookies to:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mt-3 ml-4">
                <li>Display ads on our website based on your visits to this and other websites</li>
                <li>Measure ad effectiveness and engagement</li>
                <li>Provide personalized advertising experiences</li>
                <li>Prevent fraudulent activity</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.2 Your Advertising Choices</h3>
              <p className="text-gray-700 leading-relaxed">
                You can opt out of personalized advertising by visiting:{' '}
                <a 
                  href="https://www.google.com/settings/ads" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 underline"
                >
                  Google Ads Settings
                </a>
              </p>
              <p className="text-gray-700 leading-relaxed mt-3">
                You can also visit the Digital Advertising Alliance's opt-out page at:{' '}
                <a 
                  href="http://www.aboutads.info/choices/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 underline"
                >
                  AboutAds.info
                </a>
              </p>
            </section>

            {/* How We Use Information */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. How We Use Your Information</h2>
              <p className="text-gray-700 leading-relaxed">
                We use the collected information for various purposes:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mt-3 ml-4">
                <li>To provide and maintain our website</li>
                <li>To improve user experience and website functionality</li>
                <li>To analyze website traffic and usage patterns</li>
                <li>To display relevant advertisements through Google AdSense</li>
                <li>To detect, prevent, and address technical issues</li>
                <li>To comply with legal obligations</li>
              </ul>
            </section>

            {/* Third-Party Services */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Third-Party Services</h2>
              <p className="text-gray-700 leading-relaxed">
                Our website uses third-party services that may collect information used to identify you:
              </p>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">5.1 Google AdSense</h3>
              <p className="text-gray-700 leading-relaxed">
                Privacy Policy:{' '}
                <a 
                  href="https://policies.google.com/privacy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 underline"
                >
                  https://policies.google.com/privacy
                </a>
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">5.2 External Links</h3>
              <p className="text-gray-700 leading-relaxed">
                Our website contains links to external resources and support organizations. We are not responsible 
                for the privacy practices of these third-party websites. We encourage you to read their privacy 
                policies before providing any personal information.
              </p>
            </section>

            {/* Data Retention */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Retention</h2>
              <p className="text-gray-700 leading-relaxed">
                We retain automatically collected information for as long as necessary to fulfill the purposes 
                outlined in this Privacy Policy, unless a longer retention period is required by law.
              </p>
            </section>

            {/* Your Rights */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Your Privacy Rights</h2>
              <p className="text-gray-700 leading-relaxed">
                Depending on your location, you may have certain rights regarding your personal information:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mt-3 ml-4">
                <li>Right to access your personal information</li>
                <li>Right to rectification of inaccurate data</li>
                <li>Right to erasure ("right to be forgotten")</li>
                <li>Right to restrict processing</li>
                <li>Right to data portability</li>
                <li>Right to object to processing</li>
                <li>Right to opt-out of personalized advertising</li>
              </ul>
            </section>

            {/* Children's Privacy */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                Our website is not directed to children under the age of 13. We do not knowingly collect 
                personally identifiable information from children under 13. If you are a parent or guardian 
                and believe your child has provided us with personal information, please contact us.
              </p>
            </section>

            {/* Security */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Data Security</h2>
              <p className="text-gray-700 leading-relaxed">
                We take reasonable measures to protect your information from unauthorized access, alteration, 
                disclosure, or destruction. However, no method of transmission over the internet or electronic 
                storage is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            {/* International Transfers */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. International Data Transfers</h2>
              <p className="text-gray-700 leading-relaxed">
                Your information may be transferred to and maintained on computers located outside of your state, 
                province, country, or other governmental jurisdiction where data protection laws may differ. By 
                using our website, you consent to such transfers.
              </p>
            </section>

            {/* Changes to Policy */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Changes to This Privacy Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update our Privacy Policy from time to time. We will notify you of any changes by posting 
                the new Privacy Policy on this page and updating the "Last Updated" date at the top of this policy.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy 
                Policy are effective when they are posted on this page.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Contact Us</h2>
              <p className="text-gray-700 leading-relaxed">
                If you have any questions about this Privacy Policy or our privacy practices, please contact us:
              </p>
              <div className="bg-blue-50 rounded-lg p-6 mt-4">
                <p className="text-gray-900 font-medium">Understanding Grief</p>
                <p className="text-gray-700 mt-2">Email: support@understandinggrief.com</p>
                <p className="text-gray-700">Website: grief-guide-1.preview.emergentagent.com</p>
              </div>
            </section>

            {/* Cookie Consent Notice */}
            <section className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-lg mt-8">
              <h3 className="text-lg font-semibold text-yellow-900 mb-2">Cookie Consent</h3>
              <p className="text-yellow-800 text-sm">
                By continuing to use this website, you consent to our use of cookies and other tracking 
                technologies as described in this Privacy Policy. You can manage your cookie preferences 
                through your browser settings.
              </p>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;